import type { PolymorphicProps } from "@kobalte/core";
import type { IconTypes } from "solid-icons";
import { TbLoader2 } from "solid-icons/tb";
import { Show, splitProps, type ValidComponent } from "solid-js";
import { Dynamic } from "solid-js/web";
import { type ClassNameValue, twMerge } from "tailwind-merge";
import { useStTooltipSuppressed } from "../tooltip/StTooltip";

type ExtraProps = {
  icon?: IconTypes;
  iconClass?: string;
  noBorder?: boolean;
  noColor?: boolean;
  loading?: boolean;
  /** Native `title` tooltip; omitted when app-wide tooltip suppression is active. */
  title?: string;
};

const StButtonClasses = (split: { noColor?: boolean; noBorder?: boolean }, ...classLists: ClassNameValue[]) =>
  twMerge(
    `group/btn flex cursor-pointer items-center justify-center gap-1.5 rounded-lg p-1.5 text-xs ring-brand transition-all outline-none focus-visible:ring-2`,
    !split.noColor && "bg-panel text-primary-text hover:bg-panel-hover disabled:bg-panel disabled:text-muted-text",
    !split.noBorder && "border border-muted-border hover:border-primary-border",
    ...classLists
  );

export const StButton = <T extends ValidComponent = "button">(props: PolymorphicProps<T, ExtraProps>) => {
  const [split, rest] = splitProps(props, ["icon", "noBorder", "loading", "noColor", "iconClass", "title"]);
  const tooltipSuppressed = useStTooltipSuppressed();

  return (
    <Dynamic
      component={props.as ?? "button"}
      {...rest}
      disabled={Boolean(rest.disabled) || Boolean(split.loading)}
      title={tooltipSuppressed() ? undefined : split.title}
      class={StButtonClasses(split, props.class)}
    >
      <Show when={split.icon || split.loading}>
        <Dynamic
          component={split.loading ? TbLoader2 : split.icon}
          class={twMerge(
            "size-4 transition-transform group-hover/btn:scale-hover group-active/btn:scale-active",
            split.iconClass
          )}
          classList={{
            "animate-spin": split.loading,
          }}
        />
      </Show>

      {rest.children}
    </Dynamic>
  );
};
